import React, { useMemo } from 'react';
import type { ConfiguredCarrierCount } from '~/store/fulfillmentContext/types';
import { useCarriersFiltered, useCarrierServiceMethods } from '~/hooks/queries/useCarriersFiltered';
import { CarriersColumnCell } from './CarriersColumnCell';
import { getCarrierShortName } from '~/utils/carrierShortName';
// Minimal shape: only field used is carrierServiceMethodId
type CarrierRateMinimal = { carrierServiceMethodId?: string };

interface CarriersColumnProps {
  contextId: string;
  carriers: ConfiguredCarrierCount[];
  carrierRatesById: Record<string, CarrierRateMinimal>;
}

export const CarriersColumn: React.FC<CarriersColumnProps> = ({ contextId, carriers, carrierRatesById }) => {
  const carrierMethodMap = useCarrierServiceMethods({ view: 'summary' });
  const carrierMap = useCarriersFiltered({ view: 'summary' });

  const sortedCarriers = useMemo(
    () =>
      [...carriers].sort((a, b) => {
        const nameA = getCarrierShortName(a.carrierId, carrierMap?.[a.carrierId]);
        const nameB = getCarrierShortName(b.carrierId, carrierMap?.[b.carrierId]);
        return nameA.localeCompare(nameB);
      }),
    [carriers, carrierMap],
  );

  return (
    <>
      {sortedCarriers && sortedCarriers.length > 0
        ? sortedCarriers.map((carrier) => {
            const carrierName = getCarrierShortName(carrier.carrierId, carrierMap[carrier.carrierId]);
            const methodNames = carrier.serviceMethods
              ?.map((method) => {
                const carrierServiceMethodId = carrierRatesById[method]?.carrierServiceMethodId;
                return carrierServiceMethodId ? carrierMethodMap[carrierServiceMethodId]?.name : undefined;
              })
              .filter((name): name is string => !!name);

            return (
              <CarriersColumnCell
                key={`${contextId}-${carrier.carrierId}`}
                contextId={contextId}
                carrier={carrier}
                carrierName={carrierName}
                methodNames={methodNames || []}
              />
            );
          })
        : null}
    </>
  );
};
